import type { AdminProjectOverviewResponse } from '../../../shared/types';
import Chip from '../Chip';
import { formatDateTime, formatShortDate, quietButtonClass } from './AdminUi';

interface AdminProjectMonitoringProps {
  overview: AdminProjectOverviewResponse;
}

const decisionLabels = [
  ['use', 'Use'],
  ['maybe', 'Maybe'],
  ['remove', 'Remove'],
  ['comment', 'Comment'],
] as const;

export default function AdminProjectMonitoring({ overview }: AdminProjectMonitoringProps) {
  const countsByAsset = new Map(overview.asset_counts.map((counts) => [counts.asset_id, counts]));
  const selectsUrl = `/r/${encodeURIComponent(overview.project.id)}?view=selects`;

  return (
    <section className="mt-8 border-t border-line pt-8" aria-labelledby="review-detail-title">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-faint">Review detail</p>
          <h3 id="review-detail-title" className="mt-2 text-2xl">Decisions by person and interview</h3>
          <p className="mt-2 text-sm text-muted">
            {overview.unresolved_comment_count === 0
              ? 'There are no unresolved comments.'
              : `${overview.unresolved_comment_count} unresolved ${overview.unresolved_comment_count === 1 ? 'comment' : 'comments'}.`}
          </p>
        </div>
        <a className={quietButtonClass} href={selectsUrl} target="_blank" rel="noreferrer">Open project selects</a>
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <section aria-labelledby="reviewer-detail-title">
          <h4 id="reviewer-detail-title" className="font-mono text-[10px] uppercase tracking-[0.1em] text-faint">By reviewer</h4>
          {overview.reviewers.length === 0 ? (
            <p className="mt-3 text-sm text-muted">No one has contributed yet.</p>
          ) : (
            <ul className="mt-3 divide-y divide-line border-y border-line">
              {overview.reviewers.map((reviewer) => (
                <li key={reviewer.id} className="grid gap-3 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                  <div>
                    <p className="text-sm font-semibold">
                      {reviewer.display_name}
                      {reviewer.is_owner ? <span className="owner-badge ml-2 rounded-full px-2 py-1 font-mono text-[9px] uppercase tracking-[0.08em]">Owner</span> : null}
                    </p>
                    <p className="mt-1 font-mono text-[10px] text-faint">
                      Joined {formatShortDate(reviewer.created_at)} · {reviewer.finished_at ? `Finished ${formatDateTime(reviewer.finished_at)}` : 'Reviewing'}
                    </p>
                    {/* An edit-access session changed some of these decisions, so the counts above are not all their own call. */}
                    {reviewer.adjusted.count > 0 ? (
                      <p className="mt-1 font-mono text-[10px] text-warning">
                        {reviewer.adjusted.count} adjusted by {reviewer.adjusted.by.join(', ')}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2 sm:justify-end">
                    {decisionLabels.map(([key, label]) => (
                      <Chip key={key} tone={key === 'use' ? 'success' : key === 'maybe' ? 'warning' : key === 'remove' ? 'error' : 'plain'}>
                        {label} {reviewer.counts[key]}
                      </Chip>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-faint">By interview</p>
          {overview.assets.length === 0 ? <p className="mt-3 text-sm text-muted">Interview decision counts will appear here.</p> : (
            <ul className="mt-3 divide-y divide-line border-y border-line">
              {overview.assets.map((asset) => {
                const counts = countsByAsset.get(asset.id) ?? { asset_id: asset.id, use: 0, maybe: 0, remove: 0, comment: 0 };
                return (
                  <li key={asset.id} className="grid gap-3 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                    <p className="font-semibold">{asset.title}</p>
                    <dl className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted">
                      {decisionLabels.map(([key, label]) => (
                        <div key={key} className="flex gap-1"><dt>{label}</dt><dd className="font-mono text-fg">{counts[key]}</dd></div>
                      ))}
                    </dl>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
